'use client';

import { matchesPublicCategoryProductsSeed, type PublicCategoryProductsSeed } from '@eliadmin/core/src/components/categories/public-category-products-seed';
import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import CategorySort from '@eliadmin/core/src/components/categories/category-sort';
import { ProductsGrid } from '@eliadmin/core/src/components/product/products-grid';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalNavigation, useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQueryClient, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { getProductLabel } from '../../utils/get-product-label';
import { CategoryFilterSheet } from '@eliadmin/core/src/components/categories/category-filter-sheet';
import { CollectionPageSchema } from '@eliadmin/core/src/components/seo/product-list-schema';

export type CategoryProps = {
  initialProducts?: PublicCategoryProductsSeed;
  className?: {
    grid_wrapper?: string;
    product?: string;
  };
  category: Category;
  onProductTotalChange?: (total: number) => void;
  paginationProps?: { scrollToElementAfterPageChange?: string };
};

export function CategoryProducts(props: CategoryProps) {
  const isInitialized = useCustomerStore(state => state.isInitialized);
  const searchParams = useSearchParams();
  // CategoryParamsSync still hydrates URL filters in an effect. Preserve the
  // existing filtered-page gate instead of streaming an unfiltered result set.
  const hasUrlFilters = [
    'manufacturer',
    'availability',
    'attribute',
    'order',
    'orderBy',
    'price_min',
    'price_max',
    'discounted',
    'supplier',
  ].some(key => searchParams.has(key));
  if (hasUrlFilters && !isInitialized) return <CategoryProductsSkeleton />;

  return <CategoryProductsContent {...props} />;
}

function CategoryProductsContent(props: CategoryProps) {
  // Public products must render before /api/me resolves. Authenticated prices
  // use a separate query key and fetchProducts' no-store request after login.
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const filters = useCategoryStore(s => s.filter);
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();

  const pageParam = searchParams.get('page') ?? '1';
  const parsedPage = /^[1-9]\d*$/.test(pageParam) ? Number(pageParam) : 1;
  const currentPage = Number.isSafeInteger(parsedPage) ? parsedPage : 1;

  const { t } = useTranslations();

  const generalLayout = useGlobalStore(s => s.generalLayout);

  const fakeCategories = [335, 336];

  const getCategories = () => {
    if (fakeCategories.includes(props.category.id)) {
      return [];
    }
    return [props.category.id];
  };

  const getTags = () => {
    if (props.category.id === 335) {
      return [];
    }
    if (props.category.id === 336) {
      return [1];
    }

    return [];
  };

  const queryWithoutPage = buildProductsQuery({
    categories: getCategories(),
    manufacturers: filters.manufacturers ?? [],
    results: generalLayout?.pagination?.paginationProducts || 12,
    order: filters.sort.order,
    orderBy: filters.sort.orderBy,
    attributes: filters.attributes,
    price_from: filters.price_min,
    price_to: filters.price_max,
    availabilities: filters.availabilities,
    suppliers: filters.suppliers,
    tags: getTags(),
    ...(props.category.id === 335 ? { discounted: true } : {}),
  });

  const productRequestUri = buildProductsRequest({
    ...queryWithoutPage,
    page: currentPage,
  }).uri;
  const productResetUri = buildProductsRequest({
    ...{
      ...queryWithoutPage,
      page: currentPage,
    },
    page: undefined,
  }).uri;

  const productQueryKey = [
    CacheKey.PRODUCTS,
    'infinite',
    locale,
    authToken ?? null,
    currentPage,
    productRequestUri,
  ];

  const productsQuery = useSuspenseInfiniteQuery({
    initialData: matchesPublicCategoryProductsSeed(props.initialProducts, {
      locale: locale, requestUri: productRequestUri, authToken, page: Number(currentPage ?? 1),
    }) ? { pages: [props.initialProducts!.data], pageParams: [Number(currentPage ?? 1)] } : undefined,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    initialDataUpdatedAt: props.initialProducts?.updatedAt,
    queryKey: productQueryKey,
    queryFn: ({ pageParam }) => {
      return fetchProducts({
        query: {
          ...queryWithoutPage,
          page: pageParam,
        },
        locale,
        authToken,
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: currentPage,
  });

  const isGlobalNavigating = useGlobalNavigation();

  const lastPage = productsQuery.data?.pages[productsQuery.data?.pages.length - 1];
  const flatList = productsQuery.data?.pages.flatMap(page => page.data) ?? [];

  useEffect(() => {
    props.onProductTotalChange?.(lastPage?.meta?.total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage?.meta?.total]);

  return (
    <>
      <div className="flex w-full flex-col items-center justify-center gap-4 md:hidden">
        <CategoryFilterSheet />

        <CategorySort />
        <p className="w-full whitespace-nowrap text-center text-sm font-light text-foreground">
          {productsQuery.data.pages[0].meta.total}{' '}
          {getProductLabel(productsQuery.data.pages[0].meta.total)}
        </p>
      </div>
      <div className="hidden w-full items-center justify-between md:flex">
        <p className="whitespace-nowrap text-left text-sm font-light text-foreground">
          {productsQuery.data.pages[0].meta.total}{' '}
          {getProductLabel(productsQuery.data.pages[0].meta.total)}
        </p>
        <CategorySort />
      </div>
      <div
        className={cn('relative flex w-full flex-col gap-6', 'ea-category-products', {
          'col-span-12': !templateSettings.category?.left_filter?.enabled,
        })}
      >
        {!flatList?.length && <p className="text-center text-muted-foreground">{t('text554')}</p>}
        {!!flatList.length && (
          <CollectionPageSchema
            category={props.category}
            products={flatList}
            order={filters.sort.order ?? 'desc'}
            url={props.category.breadcrumb?.slug || props.category.seo?.['og:url']}
            currency={(generalLayout?.currency_code || generalLayout?.currency)}
          />
        )}
        <ProductsGrid
          products={flatList ?? []}
          className={{
            wrapper: cn(
              'grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 sm:gap-y-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3',
              'ea-category-products__grid',
              props.className?.grid_wrapper,
            ),
            product: {
              main: cn(props.className?.product),
            },
          }}
        />

        {!!productsQuery.hasNextPage && (
          <div className={cn('flex flex-row justify-center', 'ea-category-products__load-more')}>
            <Button
              variant="dark"
              isLoading={productsQuery.isFetchingNextPage}
              onClick={() => productsQuery.fetchNextPage()}
              className="w-full sm:w-fit"
            >
              {t('text1715')}
            </Button>
          </div>
        )}

        <div
          className={cn('flex w-full flex-row justify-center', 'ea-category-products__pagination')}
        >
          <Pagination
            links={lastPage?.meta.links}
            onPageClicked={page => {
              if (String(page) !== String(currentPage)) return;
              queryClient.resetQueries({
                queryKey: productQueryKey,
                exact: true,
              });
            }}
            scrollToElementAfterPageChange={
              props.paginationProps?.scrollToElementAfterPageChange || undefined
            }
            resetWhen={[productResetUri]}
          />
        </div>

        {!!isGlobalNavigating && (
          <div
            className={cn(
              'absolute inset-0 z-10 bg-background/30',
              'ea-category-products__loading-overlay',
            )}
          ></div>
        )}
      </div>
    </>
  );
}

type CategoryProductsSkeletonProps = {
  className?: {
    container?: string;
    items_wrapper?: string;
    item?: string;
  };
  numberOfSkeletons?: number;
};

export function CategoryProductsSkeleton({
  className,
  numberOfSkeletons,
}: CategoryProductsSkeletonProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);

  const array = Array.from({ length: numberOfSkeletons || 6 }, (_, i) => i);

  return (
    <div
      className={cn(
        'relative col-span-12 flex w-full flex-col gap-4 @container',
        'ea-category-products',
        {
          'col-span-12': !templateSettings.category?.left_filter?.enabled,
        },
        className?.container,
      )}
    >
      <div
        className={cn(
          'grid w-full gap-4 @sm:grid-cols-1 @lg:grid-cols-2 @2xl:grid-cols-3',
          className?.items_wrapper,
        )}
      >
        {array.map(i => (
          <Skeleton key={i} className={cn('col-span-1 min-h-[400px] w-full', className?.item)} />
        ))}
      </div>

      <Skeleton className="h-20 w-full" />
    </div>
  );
}
